import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Home, PenLine, Wind, ClipboardList, MoreHorizontal, BarChart3, BookOpen, LogOut, X, Quote, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Hem", icon: Home },
  { path: "/dagbok", label: "Dagbok", icon: PenLine },
  { path: "/ovningar", label: "Övningar", icon: Wind },
  { path: "/tester", label: "Tester", icon: ClipboardList },
];

const MORE_ITEMS = [
  { path: "/progress", label: "Min resa", icon: BarChart3 },
  { path: "/recensioner", label: "Recensioner", icon: MessageCircle },
  { path: "/citat", label: "Citat", icon: Quote },
];

export const MobileBottomNav = () => {
  const [showMore, setShowMore] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user || !user.has_paid) return null;

  const isActive = (path) => location.pathname === path;
  const isMoreActive = MORE_ITEMS.some(item => location.pathname === item.path);

  const handleNavigate = (path) => {
    navigate(path);
    setShowMore(false);
  };

  const handleLogout = async () => {
    setShowMore(false);
    await logout();
    navigate('/');
  };

  return (
    <>
      {/* Spacer to prevent content from being hidden behind bottom nav */}
      <div className="h-16 md:hidden" />

      {/* More menu overlay */}
      <AnimatePresence>
        {showMore && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-[60] md:hidden"
              onClick={() => setShowMore(false)}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed bottom-16 left-0 right-0 z-[61] md:hidden bg-white border-t border-[#E8E0DB] rounded-t-2xl shadow-lg"
            >
              <div className="p-4 space-y-1">
                <div className="flex items-center justify-between mb-2 px-2">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#111111]/40 font-medium">Mer</span>
                  <button onClick={() => setShowMore(false)} className="p-1">
                    <X size={16} className="text-[#111111]/40" />
                  </button>
                </div>
                {MORE_ITEMS.map(item => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${
                      isActive(item.path) ? 'bg-[#F9F3EF] text-[#A0655A]' : 'text-[#111111]/70 hover:bg-[#F9F3EF]/50'
                    }`}
                    data-testid={`mobile-more-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon size={20} />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                ))}
                <div className="border-t border-[#EBE3DF] my-2" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[#A0655A] hover:bg-[#F9F3EF]/50 transition-colors"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut size={20} />
                  <span className="text-sm font-medium">Logga ut</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom nav bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-[59] md:hidden bg-white/95 backdrop-blur-md border-t border-[#E8E0DB]" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16 px-2">
          {NAV_ITEMS.map(item => {
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[56px] py-1 transition-colors ${
                  active ? 'text-[#A0655A]' : 'text-[#111111]/40'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <item.icon size={20} strokeWidth={active ? 2.2 : 1.8} />
                <span className={`text-[10px] ${active ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
              </button>
            );
          })}
          <button
            onClick={() => setShowMore(!showMore)}
            className={`flex flex-col items-center justify-center gap-0.5 min-w-[56px] py-1 transition-colors ${
              isMoreActive || showMore ? 'text-[#A0655A]' : 'text-[#111111]/40'
            }`}
            data-testid="mobile-nav-more"
          >
            <MoreHorizontal size={20} strokeWidth={isMoreActive || showMore ? 2.2 : 1.8} />
            <span className={`text-[10px] ${isMoreActive || showMore ? 'font-semibold' : 'font-medium'}`}>Mer</span>
          </button>
        </div>
      </nav>
    </>
  );
};
